import { DateTime } from 'luxon';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Badge,
  ProgressBar,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  createRiskFreePlays,
  getRiskFreePlays,
  getCampaign,
  getCampaignBudget,
  updateCampaign,
  voidRiskFreePlay,
} from '../../../apis/campaigns.api';
import { useAuth } from '../../../contexts/auth.context';
import { formatDate } from '../../../utils/helpers';
import { UTC_INPUT_HINT, isoToUtcInput, utcInputToIso } from '../../../utils/utcDateTime';
import CampaignBreadcrumb from './CampaignBreadcrumbs';
import CreateRiskFreePlayModal from './CreateRiskFreePlaysModal';
import ExportCSVButton from '../../shared-components/ExportButton';
import GameName from '../../shared-components/GameName';
import { gameName } from '../../../utils/gameNames';
import {
  buildCampaignUpdateBody,
  campaignFrozenCopy,
  campaignIsFrozen,
  campaignUpdateErrorCopy,
  validateCampaignEdit,
} from './campaignEdit';

const exportColumns = [
  { header: 'Wallet ID', key: 'walletId', forceText: true },
  { header: 'Amount', key: 'solAmountString' },
  { header: 'Played', key: 'playAmountString' },
  { header: 'Game', key: 'gameId', format: gameName },
  { header: 'Game ID', key: 'gameId', forceText: true },
  { header: 'Status', key: 'status' },
  { header: 'Transaction Signature', key: 'signature', forceText: true },
  { header: 'Reason', key: 'reason', richText: true },
  { header: 'Created At', key: 'createdAt' },
  { header: 'Modified At', key: 'modifiedAt' },
  { header: 'Expires At', key: 'expireAt' },
];

// Bar colour by share of the cap claimed (the shared card contract, on
// Bootstrap's four variants): green below two thirds, yellow to 90 %, red past.
export const budgetVariant = share =>
  share >= 0.9 ? 'danger' : share >= 2 / 3 ? 'warning' : 'success';

const fmtSol = value => {
  const n = Number(value);
  return Number.isFinite(n) ? String(Number(n.toFixed(4))) : '—';
};

/** The claims budget of an `allocated` (waitlist) campaign, from
 *  GET /campaigns/{id}/budget. Until aegis ships the route the read 404s —
 *  "unavailable", never a crash. */
export const CampaignBudget = ({ budget, state, solLimit }) => {
  if (state === 'loading') {
    return (
      <div className="text-muted small mb-2">
        <Spinner as="span" animation="border" size="sm" className="me-2" />
        Loading the claims budget…
      </div>
    );
  }
  if (state !== 'ready' || !budget) {
    return (
      <div className="text-muted small mb-2">
        Budget unavailable — the campaign budget route did not answer. Claims and the SOL left
        cannot be shown yet.
      </div>
    );
  }
  const limit = Number(budget.solLimit ?? solLimit);
  const allocated = Number(budget.allocatedSol ?? 0);
  const hasLimit = Number.isFinite(limit) && limit > 0;
  const share = hasLimit ? allocated / limit : 0;
  const remaining =
    budget.remainingSol != null ? Number(budget.remainingSol) : hasLimit ? limit - allocated : null;
  const spent = remaining != null && remaining <= 0;
  return (
    <div className="mb-2">
      <ProgressBar
        now={Math.min(100, share * 100)}
        label={`${fmtSol(allocated)} / ${hasLimit ? fmtSol(limit) : '∞'} SOL claimed`}
        variant={spent ? 'danger' : budgetVariant(share)}
        style={{ maxWidth: '300px' }}
        className="mb-1"
      />
      <div className="small text-muted">
        {budget.allocatedCount ?? 0} claims
        {remaining != null ? ` · ${fmtSol(remaining)} SOL left` : ''}
        {budget.expireAt ? ` · redeem by ${formatDate(budget.expireAt)}` : ''}
      </div>
      {spent && <Badge bg="danger">Budget claimed</Badge>}
    </div>
  );
};

const capRemainingCopy = (campaign, budget, budgetState) => {
  const allocated = Number(budget?.allocatedSol);
  const remaining =
    budget?.remainingSol != null
      ? Number(budget.remainingSol)
      : Number.isFinite(allocated) &&
        Number.isFinite(Number(budget?.solLimit ?? campaign?.solLimit))
      ? Number(budget?.solLimit ?? campaign?.solLimit) - allocated
      : null;
  const budgetReady = budgetState === 'ready' && Number.isFinite(allocated);
  return budgetReady
    ? `${fmtSol(allocated)} SOL allocated · ${
        remaining != null ? `${fmtSol(remaining)} SOL remaining` : 'remaining unknown'
      }`
    : 'Allocated / remaining show once the budget route answers.';
};

/** Popup for Cap (solLimit) + expiry on an allocated campaign.
 *  Extend-only expiry (input min = current expireAt). Soft freeze
 *  (`expireAt − 2h` / Aegis mutable / mutableReason / softFreezeAt)
 *  disables Save and shows the frozen / too-late copy. */
export const CampaignCapExpiryModal = ({
  show,
  onHide,
  campaign,
  budget,
  budgetState,
  frozen,
  frozenCopy,
  saving,
  error,
  onSave,
}) => {
  const [solLimit, setSolLimit] = useState('');
  const [expireAt, setExpireAt] = useState('');

  useEffect(() => {
    if (!show) return;
    setSolLimit(campaign?.solLimit != null ? String(campaign.solLimit) : '');
    setExpireAt(isoToUtcInput(campaign?.expireAt));
  }, [show, campaign?.solLimit, campaign?.expireAt]);

  const handleSave = async () => {
    const ok = await onSave({ solLimit, expireAt });
    if (ok) onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered animation={false}>
      <Modal.Header closeButton>
        <Modal.Title>Edit cap and expiry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {frozen && (
          <div className="text-danger small mb-2" role="alert">
            {frozenCopy || campaignFrozenCopy(campaign)}
          </div>
        )}
        <Form.Group className="mb-2">
          <Form.Label>Cap (SOL)</Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            value={solLimit}
            onChange={e => setSolLimit(e.target.value)}
            disabled={frozen || saving}
            readOnly={frozen}
            aria-label="campaign cap"
          />
          <Form.Text className="text-muted">
            {capRemainingCopy(campaign, budget, budgetState)}
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Expiry</Form.Label>
          <Form.Control
            type="datetime-local"
            value={expireAt}
            min={isoToUtcInput(campaign?.expireAt) || undefined}
            onChange={e => setExpireAt(e.target.value)}
            disabled={frozen || saving}
            readOnly={frozen}
            aria-label="campaign expiry"
          />
          <Form.Text className="text-muted">{UTC_INPUT_HINT}</Form.Text>
          <Form.Text className="text-muted">Expiry can only be extended.</Form.Text>
        </Form.Group>
        {error && (
          <div className="text-danger small mt-1" role="alert">
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={frozen || saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save cap and expiry'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** Cap (solLimit) + expiry on an allocated (claims-cap) campaign.
 *  Detail is read-only; Edit opens the popup. Remaining / allocated
 *  sit next to the values. Text and controls only — the surrounding
 *  panel is unchanged. */
export const CampaignCapExpiryEdit = ({
  campaign,
  budget,
  budgetState,
  frozen,
  frozenCopy,
  saving,
  error,
  success,
  onSave,
}) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="mb-2">
      {frozen && (
        <div className="small text-muted mb-2">{frozenCopy || campaignFrozenCopy(campaign)}</div>
      )}
      <div className="mb-1">
        Cap {campaign?.solLimit != null ? `${fmtSol(campaign.solLimit)} SOL` : '—'}
      </div>
      <div className="mb-1">Expiry {campaign?.expireAt ? formatDate(campaign.expireAt) : '—'}</div>
      <div className="small text-muted mb-2">{capRemainingCopy(campaign, budget, budgetState)}</div>
      <Button size="sm" aria-label="Edit cap and expiry" onClick={() => setShowModal(true)}>
        Edit
      </Button>
      {success && <div className="text-success small mt-1">{success}</div>}
      <CampaignCapExpiryModal
        show={showModal}
        onHide={() => setShowModal(false)}
        campaign={campaign}
        budget={budget}
        budgetState={budgetState}
        frozen={frozen}
        frozenCopy={frozenCopy}
        saving={saving}
        error={error}
        onSave={onSave}
      />
    </div>
  );
};

const Campaign = () => {
  const { campaignId } = useParams();
  const [search, setSearch] = useState('');
  const [filteredRiskFreePlays, setFilteredRiskFreePlays] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [riskFreePlays, setRiskFreePlays] = useState([]);
  const [campaign, setCampaign] = useState(null);
  // Waitlist cap + expiry: `allocated` campaigns count claims (RFPs minted),
  // not plays. Their budget comes from aegis, not from summing the rows here.
  const isAllocated = campaign?.capMode === 'allocated';
  const [budget, setBudget] = useState(null);
  const [budgetState, setBudgetState] = useState('idle'); // idle | loading | ready | unavailable
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);

  useEffect(() => {
    authToken.current = auth?.idToken;
  }, [auth?.idToken]);

  const fetchData = useCallback(async () => {
    const response = await getRiskFreePlays(campaignId, authToken.current);
    setRiskFreePlays(
      response.map(rfp => ({
        ...rfp,
        solAmountString: `${rfp.solAmount} SOL`,
        playAmountString: `${rfp.playAmount ? `${rfp.playAmount} SOL` : ''}`,
      }))
    );
  }, [campaignId]);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const campaign = await getCampaign(campaignId);
        setCampaign(campaign);
      } catch (error) {
        console.error('Error fetching campaign data:', error);
      }
    };

    fetchData();
    fetchCampaign();
  }, [campaignId, fetchData]);

  // One reader for the first load and every refresh (a grant or a void made
  // on this page moves the budget). A stale answer never lands: only the
  // latest request may write. A refresh keeps the bar on screen — "loading"
  // shows only while there is nothing to show yet.
  const budgetRequest = useRef(0);
  const loadBudget = useCallback(async () => {
    const requestId = ++budgetRequest.current;
    setBudgetState(prev => (prev === 'ready' ? prev : 'loading'));
    try {
      const answer = await getCampaignBudget(campaignId, authToken.current);
      if (requestId !== budgetRequest.current) return;
      setBudget(answer ?? null);
      setBudgetState(answer ? 'ready' : 'unavailable');
    } catch (error) {
      // 404 until the aegis route ships; any failure reads "unavailable".
      console.error('Error fetching campaign budget:', error?.response?.status ?? error);
      if (requestId === budgetRequest.current) setBudgetState('unavailable');
    }
  }, [campaignId]);

  useEffect(() => {
    if (!isAllocated) return;
    loadBudget();
  }, [isAllocated, loadBudget]);

  useEffect(() => {
    const filterRiskFreePlays = () => {
      const searchLower = search.toLowerCase();
      const filtered = riskFreePlays.filter(play =>
        `${JSON.stringify(play)} ${gameName(play.gameId)}`.toLowerCase().includes(searchLower)
      );
      setFilteredRiskFreePlays(filtered);
    };

    filterRiskFreePlays();
  }, [search, riskFreePlays]);

  const onCreateRiskFreePlays = async formData => {
    try {
      const response = await createRiskFreePlays(campaignId, formData, authToken.current);

      if (response) {
        console.log('Risk-free play created:', response);
        setRiskFreePlays([...response, ...riskFreePlays]);
        // The grants just minted count against a claims cap.
        if (isAllocated) loadBudget();
      } else {
        const errorMessage = response;
        console.error('Error creating risk-free play:', errorMessage);
        alert('Error creating risk-free play. Please try again.');
      }
    } catch (error) {
      console.error('Error creating risk-free play:', error);
      alert('Error creating risk-free play. Please try again.');
    }
  };

  // Mirrors the server-side guard: only AVAILABLE (granted-but-never-activated,
  // not yet time-expired) RFP grants can be voided. ACTIVATED/PROCESSING/
  // REWARDED/REDEEMED/EXPIRED rows are locked/processed and the server rejects
  // them with a 400.
  const canVoidRiskFreePlay = play => {
    if (play.status !== 'AVAILABLE') return false;
    const isExpired = play.expireAt && DateTime.fromISO(play.expireAt) < DateTime.utc();
    return !isExpired;
  };

  const onVoidRiskFreePlay = async play => {
    const confirmed = window.confirm(
      `Remove this RFP grant? This cannot be undone.\n\n` +
        `Wallet: ${play.walletId}\n` +
        `Amount: ${play.solAmountString}`
    );
    if (!confirmed) return;

    try {
      await voidRiskFreePlay(campaignId, play.id, authToken.current);
    } catch (error) {
      // Only a failed void reaches here — axios rejects non-2xx.
      console.error('Error removing risk-free play:', error);
      alert('Error removing risk-free play. Please try again.');
      return;
    }

    // Void succeeded. A refetch failure must not report the removal as failed.
    alert('RFP grant removed.');
    try {
      await fetchData();
    } catch (refreshError) {
      console.error('Error refreshing risk-free plays after void:', refreshError);
    }
    // A voided grant is EXPIRED in aegis and leaves the claims budget.
    if (isAllocated) loadBudget();
  };

  const onSaveCapExpiry = async ({ solLimit: rawLimit, expireAt: expireInput }) => {
    setEditError('');
    setEditSuccess('');
    const parsedSolLimit = parseFloat(String(rawLimit ?? '').trim());
    const expireAtIso = utcInputToIso(expireInput);
    const allocated = Number(budget?.allocatedSol ?? campaign?.allocatedSol);
    const body = buildCampaignUpdateBody(
      { solLimit: parsedSolLimit, expireAt: expireAtIso },
      campaign
    );
    if (!Object.keys(body).length) {
      setEditError('Change the cap or the expiry before saving.');
      return false;
    }
    const problem = validateCampaignEdit({
      solLimit: parsedSolLimit,
      expireAt: expireAtIso,
      allocatedSol: Number.isFinite(allocated) ? allocated : undefined,
      currentExpireAt: campaign?.expireAt,
      changing: { solLimit: body.solLimit != null, expireAt: Boolean(body.expireAt) },
    });
    if (problem) {
      setEditError(problem);
      return false;
    }
    setEditSaving(true);
    try {
      const updated = await updateCampaign(campaignId, body, authToken.current);
      if (updated) {
        setCampaign(prev => ({ ...prev, ...updated }));
        // 200 carries allocatedSol / allocatedCount — keep the budget line
        // in step before GET /budget refreshes.
        if (
          updated.allocatedSol != null ||
          updated.allocatedCount != null ||
          updated.solLimit != null ||
          updated.expireAt
        ) {
          setBudget(prev => {
            if (!prev) return prev;
            const solLimit = updated.solLimit ?? prev.solLimit;
            const allocatedSol = updated.allocatedSol ?? prev.allocatedSol;
            const remaining =
              Number.isFinite(Number(solLimit)) && Number.isFinite(Number(allocatedSol))
                ? Number(solLimit) - Number(allocatedSol)
                : prev.remainingSol;
            return {
              ...prev,
              solLimit,
              expireAt: updated.expireAt ?? prev.expireAt,
              allocatedSol,
              allocatedCount: updated.allocatedCount ?? prev.allocatedCount,
              remainingSol: remaining,
            };
          });
        }
      } else {
        const fresh = await getCampaign(campaignId);
        setCampaign(fresh);
      }
      if (isAllocated) loadBudget();
      setEditSuccess('Cap and expiry saved.');
      return true;
    } catch (error) {
      console.error('Error updating campaign cap/expiry:', error);
      setEditError(campaignUpdateErrorCopy(error));
      return false;
    } finally {
      setEditSaving(false);
    }
  };

  const getStatusBadge = (status, expireAt) => {
    const isExpired = expireAt && DateTime.fromISO(expireAt) < DateTime.utc();

    if (isExpired && (status === 'AVAILABLE' || status === 'ACTIVATED')) {
      return <Badge bg="danger">EXPIRED</Badge>;
    }

    switch (status) {
      case 'REWARDED':
        return <Badge bg="success">REWARDED</Badge>;
      case 'REDEEMED':
        return <Badge bg="warning">REDEEMED</Badge>;
      case 'AVAILABLE':
        return <Badge bg="primary">AVAILABLE</Badge>;
      case 'EXPIRED':
        return <Badge bg="danger">EXPIRED</Badge>;
      case 'ACTIVATED':
        return <Badge bg="info">ACTIVATED</Badge>;
      // type
      case 'GOOD_TIL_WIN':
        return <Badge bg="secondary">Good 'til Win</Badge>;
      default:
        return <Badge bg="dark">ACTIVATED</Badge>;
    }
  };

  const renderStatusBadge = expireAt => {
    const isExpired = DateTime.fromISO(expireAt) < DateTime.utc();

    return <Badge bg={isExpired ? 'danger' : 'success'}>{isExpired ? 'Expired' : 'Active'}</Badge>;
  };

  const computeRedeemedSol = rfps => {
    const redeemedStatuses = ['PROCESSING', 'REWARDED', 'REDEEMED'];
    return rfps
      .filter(r => redeemedStatuses.includes(r.status))
      .reduce((sum, r) => {
        const amount = Number(r.solAmount);
        return sum + (Number.isNaN(amount) ? 0 : amount);
      }, 0);
  };

  const redeemedSol = computeRedeemedSol(riskFreePlays);
  const rawSolLimit = campaign?.solLimit;
  const parsedSolLimit = Number(rawSolLimit);
  const hasSolLimit =
    rawSolLimit !== null && rawSolLimit !== undefined && !Number.isNaN(parsedSolLimit);
  const solLimitValue = hasSolLimit ? parsedSolLimit : null;
  const solLimitProgress = hasSolLimit
    ? solLimitValue <= 0
      ? redeemedSol > 0
        ? 100
        : 0
      : Math.min(100, (redeemedSol / solLimitValue) * 100)
    : 0;
  const isLimitReached = hasSolLimit && (solLimitValue <= 0 || redeemedSol >= solLimitValue);

  return (
    <Container>
      <CampaignBreadcrumb currentName={campaign?.name} />
      {campaign && (
        <>
          <Row className="game-summary-panel">
            <Col>
              <div className="game-micro">campaign detail</div>
              <h2>{campaign?.name}</h2>
              {!isAllocated && <h6>Expires at {formatDate(campaign.expireAt)}</h6>}
              <h6>Created by {campaign.createdBy}</h6>
              {!isAllocated && (
                <h6>SOL Limit: {hasSolLimit ? `${solLimitValue} SOL` : 'Unlimited'}</h6>
              )}
              {isAllocated ? (
                <>
                  <h6>
                    Cap counts{' '}
                    <Badge bg="info" text="dark">
                      claims (waitlist)
                    </Badge>
                  </h6>
                  <CampaignBudget budget={budget} state={budgetState} solLimit={solLimitValue} />
                  <CampaignCapExpiryEdit
                    campaign={campaign}
                    budget={budget}
                    budgetState={budgetState}
                    frozen={campaignIsFrozen(campaign)}
                    frozenCopy={campaignFrozenCopy(campaign)}
                    saving={editSaving}
                    error={editError}
                    success={editSuccess}
                    onSave={onSaveCapExpiry}
                  />
                </>
              ) : (
                hasSolLimit && (
                  <>
                    <ProgressBar
                      now={solLimitProgress}
                      label={`${redeemedSol.toFixed(2)} / ${solLimitValue} SOL`}
                      variant={isLimitReached ? 'danger' : 'primary'}
                      style={{ maxWidth: '300px' }}
                      className="mb-2"
                    />
                    {isLimitReached && <Badge bg="danger">Limit Reached</Badge>}
                  </>
                )
              )}
              <h6>{renderStatusBadge(campaign.expireAt)}</h6>
            </Col>
          </Row>
          <Row className="game-toolbar">
            <Col>
              <Form.Control
                type="text"
                placeholder="Search Risk Free Plays"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </Col>
            <Col className="col-auto">
              <Button
                disabled={DateTime.fromISO(campaign?.expireAt) < DateTime.utc()}
                onClick={() => setShowModal(true)}
              >
                Create Risk Free Play
              </Button>
              <CreateRiskFreePlayModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onCreate={onCreateRiskFreePlays}
              />
            </Col>
            <Col className="col-auto">
              <ExportCSVButton data={filteredRiskFreePlays} columns={exportColumns} />
            </Col>
          </Row>
        </>
      )}
      <Row>
        <Col>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Wallet ID</th>
                <th>Amount</th>
                <th>Played</th>
                <th>Game</th>
                <th>Status</th>
                {/* <th>Source</th> */}
                {/* <th>Created by</th> */}
                {/* <th>Type</th> */}
                <th>Tx</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRiskFreePlays.map(play => (
                <tr key={play.id}>
                  {/* <td>{play.id}</td> */}
                  {/* <td>{play.campaignName}</td> */}
                  <td>{play.walletId}</td>
                  <td>{play.solAmountString}</td>
                  <td>{play.playAmountString}</td>
                  <td>
                    <GameName gameId={play.gameId} />
                  </td>
                  <td>{getStatusBadge(play.status, play.expireAt)}</td>
                  {/* <td>{play.source}</td> */}
                  {/* <td>{play.createdBy?.slice(0, 4)}</td> */}
                  {/* <td>{getStatusBadge(play?.type?.split('_').join(' ') ?? 'DEFAULT')}</td> */}
                  <td>
                    {play?.signature?.length > 0 ? (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`https://solscan.io/tx/${play.signature}`}
                      >
                        <i className="d-inline-flex fas fa-external-link-alt fa-xs"></i>
                      </a>
                    ) : (
                      <></>
                    )}
                  </td>
                  <td>{play.reason?.slice(0, 10)}</td>
                  <td>
                    {canVoidRiskFreePlay(play) ? (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => onVoidRiskFreePlay(play)}
                      >
                        Remove
                      </Button>
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default Campaign;
